import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  IAiScriptCellOverridesResponse,
  IAiScriptOverrideCandidatesResponse,
} from "@/payroll/ai-script/types";
import {
  aiScriptCellOverrideHistoryKey,
  aiScriptCellOverridesKey,
  aiScriptOverrideCandidatesKey,
} from "./keys";
import { getAiScriptCellOverridesPath } from "./paths";

export const useGetAiScriptCellOverrides = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptCellOverridesKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) throw new Error("Payroll run is required");

      return api
        .get(getAiScriptCellOverridesPath(businessId, runId))
        .then((res) => res.data as IAiScriptCellOverridesResponse);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

export const useGetAiScriptCellOverrideHistory = (
  businessId: string | null,
  runId: string | null,
  overrideKey: string | null,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptCellOverrideHistoryKey(businessId, runId, overrideKey),
    queryFn: () => {
      if (!businessId || !runId || !overrideKey) {
        throw new Error("Override key is required");
      }

      return api
        .get(getAiScriptCellOverridesPath(businessId, runId), {
          params: { override_key: overrideKey },
        })
        .then((res) => res.data as IAiScriptCellOverridesResponse);
    },
    enabled: !!businessId && !!runId && !!overrideKey,
    retry: false,
  });
};

export const useGetAiScriptOverrideCandidates = (
  businessId: string | null,
  runId: string | null,
  overrideId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: aiScriptOverrideCandidatesKey(businessId, runId, overrideId),
    queryFn: () => {
      if (!businessId || !runId || !overrideId) {
        throw new Error("Override is required");
      }

      return api
        .get(
          `${getAiScriptCellOverridesPath(businessId, runId)}/${overrideId}/candidates`,
        )
        .then((res) => res.data as IAiScriptOverrideCandidatesResponse);
    },
    enabled: enabled && !!businessId && !!runId && !!overrideId,
    retry: false,
  });
};
